import { AthenaBillingCapabilityError } from "../errors.ts";
import type { BillingOperation } from "../runtime/capabilities.ts";
import { resolveBillingEnvironment } from "../runtime/environment.ts";
import type { BillingSqlExecutor } from "./repository.ts";

export async function resolveBillingConnectionForOperation(input: {
  credentialReference?: string;
  environment?: "test" | "live";
  explicitConnectionId?: string;
  operation: BillingOperation;
  ownerId?: string;
  ownerKind?: string;
  provider?: string;
  sql: BillingSqlExecutor;
  testMode?: boolean;
}): Promise<string> {
  if (input.explicitConnectionId?.trim()) {
    const result = await input.sql.query(
      `SELECT id::text AS id, owner_id, owner_kind, provider, environment, credential_reference
			 FROM billing.billing_provider_connections
			 WHERE id = $1::uuid
			   AND deleted_at IS NULL
			 LIMIT 1`,
      [input.explicitConnectionId.trim()]
    );
    const row = result.rows[0];
    if (!row || typeof row.id !== "string") {
      throw new AthenaBillingCapabilityError({
        operation: input.operation,
        reason: "provider_connection_missing",
      });
    }
    if (
      input.ownerId != null &&
      typeof row.owner_id === "string" &&
      row.owner_id !== input.ownerId
    ) {
      throw new AthenaBillingCapabilityError({
        operation: input.operation,
        reason: "provider_connection_missing",
      });
    }
    if (
      input.ownerKind != null &&
      typeof row.owner_kind === "string" &&
      row.owner_kind !== input.ownerKind
    ) {
      throw new AthenaBillingCapabilityError({
        operation: input.operation,
        reason: "provider_connection_missing",
      });
    }
    return row.id;
  }
  const environment =
    input.environment ??
    resolveBillingEnvironment({ testMode: input.testMode }).name;
  const provider = input.provider ?? "mollie";
  const filters: unknown[] = [provider, environment];
  let sql = `SELECT id::text AS id
		 FROM billing.billing_provider_connections
		 WHERE deleted_at IS NULL
		   AND status = 'active'
		   AND provider = $1
		   AND environment = $2`;
  if (input.ownerId) {
    filters.push(input.ownerId);
    sql += ` AND owner_id = $${filters.length}`;
  }
  if (input.ownerKind) {
    filters.push(input.ownerKind);
    sql += ` AND owner_kind = $${filters.length}`;
  }
  if (input.credentialReference) {
    filters.push(input.credentialReference);
    sql += ` AND credential_reference = $${filters.length}`;
  }
  sql += " LIMIT 2";
  const slotted = await input.sql.query(sql, filters);
  if (slotted.rows.length === 1 && typeof slotted.rows[0]?.id === "string") {
    return slotted.rows[0].id;
  }
  if (slotted.rows.length > 1) {
    throw new AthenaBillingCapabilityError({
      operation: input.operation,
      reason: "provider_connection_ambiguous",
    });
  }
  throw new AthenaBillingCapabilityError({
    operation: input.operation,
    reason: "provider_connection_missing",
  });
}
